// Account Search Utility
// Reusable account search and selection component with hierarchy expansion

import { apiCall } from '../core/api.js';
import { auth } from '../core/auth.js';

let accountsData = null;
let flatAccountsList = [];

/**
 * Build a flat list of all accounts for searching
 */
function buildFlatAccountsList(accounts, parentPath = [], result = []) {
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const accountName = account.account_name;
        const currentPath = [...parentPath, accountName];
        
        result.push({
            id: accountId,
            name: accountName,
            path: currentPath,
            pathString: currentPath.join(' > '),
            data: account
        });
        
        if (account.children && account.children.length > 0) {
            buildFlatAccountsList(account.children, currentPath, result);
        }
    });
    
    return result;
}

/**
 * Initialize account search component
 * @param {string} searchInputId - ID of the search input field
 * @param {string} resultsContainerId - ID of the results dropdown container
 * @param {string} treeContainerId - ID of the account tree container
 * @param {string} hiddenInputId - ID of the hidden input that stores selected account ID
 * @param {Function} onSelectCallback - Optional callback when account is selected
 */
export async function initAccountSearch(searchInputId, resultsContainerId, treeContainerId, hiddenInputId, onSelectCallback = null) {
    const searchInput = document.getElementById(searchInputId);
    const resultsContainer = document.getElementById(resultsContainerId);
    
    if (!searchInput || !resultsContainer) {
        console.error('Account search: Required elements not found');
        return;
    }
    
    // Load accounts data if not already loaded
    if (!accountsData) {
        await loadAccountsData();
    }
    
    // Set up event listeners
    let searchTimeout = null;
    
    searchInput.addEventListener('input', (e) => {
        const searchTerm = e.target.value.trim().toLowerCase();
        
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        
        searchTimeout = setTimeout(() => {
            performSearch(searchTerm, resultsContainer, treeContainerId, hiddenInputId, onSelectCallback);
        }, 300);
    });
    
    searchInput.addEventListener('focus', (e) => {
        const searchTerm = e.target.value.trim().toLowerCase();
        if (searchTerm && searchTerm.length >= 2) {
            performSearch(searchTerm, resultsContainer, treeContainerId, hiddenInputId, onSelectCallback);
        }
    });
    
    searchInput.addEventListener('blur', (e) => {
        // Don't hide if clicking inside the results container
        setTimeout(() => {
            // Check if the active element is inside the results container
            if (!resultsContainer.contains(document.activeElement)) {
                resultsContainer.classList.add('hidden');
            }
        }, 300);
    });
    
    // Prevent results container from causing blur when clicking on it
    resultsContainer.addEventListener('mousedown', (e) => {
        e.preventDefault();
    });
}

/**
 * Load accounts data from API
 */
async function loadAccountsData() {
    try {
        const response = await apiCall('/accounts/hierarchy', {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });
        
        if (response && response.accounts_tree) {
            accountsData = response.accounts_tree;
            flatAccountsList = buildFlatAccountsList(response.accounts_tree);
        }
    } catch (error) {
        console.error('Failed to load accounts for search:', error);
    }
}

/**
 * Perform search and display results
 */
function performSearch(searchTerm, resultsContainer, treeContainerId, hiddenInputId, onSelectCallback) {
    if (!searchTerm || searchTerm.length < 2) {
        resultsContainer.classList.add('hidden');
        resultsContainer.innerHTML = '';
        return;
    }
    
    // Filter accounts by name
    const matches = flatAccountsList.filter(account => 
        account.name.toLowerCase().includes(searchTerm)
    );
    
    if (matches.length === 0) {
        resultsContainer.innerHTML = `
            <div class="px-3 py-2 text-sm text-gray-500">
                No accounts found
            </div>
        `;
        resultsContainer.classList.remove('hidden');
        return;
    }
    
    // Limit results to 10
    const displayMatches = matches.slice(0, 10);
    
    const resultsHtml = displayMatches.map(account => `
        <button type="button"
                data-account-id="${account.id}"
                data-account-name="${account.name.replace(/"/g, '&quot;')}"
                class="account-search-result w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0">
            <div class="text-sm font-medium text-gray-900">${account.name}</div>
            <div class="text-xs text-gray-500">${account.pathString}</div>
        </button>
    `).join('');
    
    resultsContainer.innerHTML = resultsHtml;
    resultsContainer.classList.remove('hidden');
    
    // Add click handlers to results (using mousedown to prevent blur issues)
    resultsContainer.querySelectorAll('.account-search-result').forEach(button => {
        button.addEventListener('mousedown', (e) => {
            e.preventDefault(); // Prevent blur from firing
            const accountId = button.getAttribute('data-account-id');
            const accountName = button.getAttribute('data-account-name');
            selectAccountFromSearch(accountId, accountName, treeContainerId, hiddenInputId, resultsContainer, onSelectCallback);
        });
    });
}

/**
 * Select an account from search results
 */
function selectAccountFromSearch(accountId, accountName, treeContainerId, hiddenInputId, resultsContainer, onSelectCallback) {
    // Hide search results
    resultsContainer.classList.add('hidden');
    
    // Find the account in the flat list
    const account = flatAccountsList.find(acc => acc.id === accountId);
    if (!account) return;
    
    // Set the hidden input value
    const hiddenInput = document.getElementById(hiddenInputId);
    if (hiddenInput) {
        hiddenInput.value = accountId;
    }
    
    // Expand the hierarchy in the tree to show this account (with small delay for rendering)
    setTimeout(() => {
        expandToAccountInTree(accountId, treeContainerId);
        
        // Highlight the account
        highlightAccountInTree(accountId, treeContainerId);
        
        // Click the radio button to select the account
        selectRadioButtonForAccount(accountId, treeContainerId);
        
        // Scroll to the account
        setTimeout(() => {
            const treeContainer = document.getElementById(treeContainerId);
            if (treeContainer) {
                // Try to find by data-account-id attribute (for accounts page)
                let accountElement = treeContainer.querySelector(`[data-account-id="${accountId}"]`);
                
                // If not found, try to find by radio button value (for user/location modals)
                if (!accountElement) {
                    const radioButton = treeContainer.querySelector(`input[type="radio"][value="${accountId}"]`);
                    if (radioButton) {
                        accountElement = radioButton.closest('.account-tree-item, .flex');
                    }
                }
                
                if (accountElement) {
                    accountElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        }, 200);
    }, 50);
    
    // Call the callback if provided
    if (onSelectCallback) {
        onSelectCallback(accountId, accountName);
    }
}

/**
 * Expand hierarchy to show a specific account in the tree
 */
function expandToAccountInTree(accountId, treeContainerId) {
    const treeContainer = document.getElementById(treeContainerId);
    if (!treeContainer) return;
    
    // Find all parent account IDs
    const parentIds = findParentAccountIds(accountId, accountsData);
    if (!parentIds) return;
    
    // Determine the tree prefix based on container ID
    let treePrefix = 'tree-';
    let iconPrefix = 'tree-icon-';
    
    // Check for location create modal (locations-modals.js)
    if (treeContainerId.includes('location-account-tree') && !treeContainerId.includes('edit-location-account-tree-container')) {
        treePrefix = 'loc-tree-';
        iconPrefix = 'loc-tree-icon-';
    }
    // Check for location edit (location-edit.js main edit modal)
    else if (treeContainerId === 'edit-location-account-tree-container') {
        treePrefix = 'location-tree-';
        iconPrefix = 'location-tree-icon-';
    }
    // Check for user/other edit modals
    else if (treeContainerId.includes('edit')) {
        treePrefix = 'edit-tree-';
        iconPrefix = 'edit-tree-icon-';
    }
    
    // Expand all parents
    parentIds.forEach(parentId => {
        const childContainer = document.getElementById(`${treePrefix}${parentId}`);
        const icon = document.getElementById(`${iconPrefix}${parentId}`);
        
        if (childContainer && icon) {
            // Show the children container
            childContainer.classList.remove('hidden');
            // Rotate the icon
            icon.classList.add('rotate-90');
        }
    });
}

/**
 * Find all parent IDs for a given account
 */
function findParentAccountIds(targetId, accounts, parents = []) {
    for (const account of accounts) {
        const accountId = account._id || account.id;
        
        if (accountId === targetId) {
            return parents;
        }
        
        if (account.children && account.children.length > 0) {
            const result = findParentAccountIds(targetId, account.children, [...parents, accountId]);
            if (result) {
                return result;
            }
        }
    }
    
    return null;
}

/**
 * Select the radio button for an account in the tree
 */
function selectRadioButtonForAccount(accountId, treeContainerId) {
    const treeContainer = document.getElementById(treeContainerId);
    if (!treeContainer) return;
    
    // Find the radio button for this account
    const radioButton = treeContainer.querySelector(`input[type="radio"][value="${accountId}"]`);
    if (radioButton) {
        // Check the radio button
        radioButton.checked = true;
        
        // Trigger the onchange event to update the hidden input
        const changeEvent = new Event('change', { bubbles: true });
        radioButton.dispatchEvent(changeEvent);
    }
}

/**
 * Highlight an account temporarily in the tree
 */
function highlightAccountInTree(accountId, treeContainerId) {
    const treeContainer = document.getElementById(treeContainerId);
    if (!treeContainer) return;
    
    // Try to find by data-account-id attribute (for accounts page)
    let accountElement = treeContainer.querySelector(`[data-account-id="${accountId}"]`);
    
    // If not found, try to find by radio button value (for user/location modals)
    if (!accountElement) {
        const radioButton = treeContainer.querySelector(`input[type="radio"][value="${accountId}"]`);
        if (radioButton) {
            // Find the parent container (usually 2-3 levels up)
            accountElement = radioButton.closest('.account-tree-item, .flex');
        }
    }
    
    if (!accountElement) return;
    
    // Add highlight class
    accountElement.classList.add('bg-yellow-100');
    
    // Remove highlight after 2 seconds
    setTimeout(() => {
        accountElement.classList.remove('bg-yellow-100');
    }, 2000);
}

/**
 * Clear search results
 */
export function clearAccountSearchResults(searchInputId, resultsContainerId) {
    const searchInput = document.getElementById(searchInputId);
    const resultsContainer = document.getElementById(resultsContainerId);
    
    if (searchInput) {
        searchInput.value = '';
    }
    
    if (resultsContainer) {
        resultsContainer.classList.add('hidden');
        resultsContainer.innerHTML = '';
    }
}

/**
 * Reset cached data (call this when accounts are updated)
 */
export function resetAccountSearchCache() {
    accountsData = null;
    flatAccountsList = [];
}
